import React, {useMemo} from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@material-ui/core';
import useStore from '../../store/store';
import usePersistentStore from '../../store/persistentStore';
import {
  buildComparisonPeriod,
  getComparisonDateContext,
  getComparisonModeLabel,
  getComparisonModeOptions,
  normalizeComparisonMode,
} from '../helpers/comparisonPeriod';

function useComparisonMode({showCurrentPeriodInDateContext = false} = {}) {
  const comparisonMode = normalizeComparisonMode(
    usePersistentStore(state => state.comparisonMode)
  );
  const setPersistentState = usePersistentStore(state => state.setState);
  const period = useStore(state => state.period);
  const setState = useStore(state => state.setState);

  const modeOptions = useMemo(
    () =>
      getComparisonModeOptions(period).map(option => ({
        ...option,
        dateContext: getComparisonDateContext(period, option.value, {
          showCurrentPeriod: showCurrentPeriodInDateContext,
        }),
      })),
    [period, showCurrentPeriodInDateContext]
  );

  const setComparisonMode = mode => {
    const normalized = normalizeComparisonMode(mode);
    setPersistentState('comparisonMode', normalized);

    if (period?.startDate && period?.endDate) {
      setState(
        'comparisonPeriod',
        buildComparisonPeriod(period.startDate, period.endDate, normalized)
      );
    }

    setState('calculated', null);
  };

  return {comparisonMode, modeOptions, setComparisonMode, period};
}

export function ComparisonModeRadio({
  disabled = false,
  showCurrentPeriodInDateContext = false,
}) {
  const {comparisonMode, modeOptions, setComparisonMode} = useComparisonMode({
    showCurrentPeriodInDateContext,
  });

  return (
    <FormControl component="fieldset" disabled={disabled} style={{width: '100%'}}>
      <RadioGroup
        value={comparisonMode}
        onChange={event => setComparisonMode(event.target.value)}
        style={{gap: 8}}
      >
        {modeOptions.map(({value, label, dateContext}) => (
          <FormControlLabel
            key={value}
            value={value}
            control={<Radio color="primary" style={{padding: 4}} />}
            label={
              <div>
                <Typography variant="body2">{label}</Typography>
                {dateContext && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {dateContext}
                  </Typography>
                )}
              </div>
            }
            style={{alignItems: 'flex-start', marginLeft: -8, marginRight: 0, marginBottom: 0}}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

export function ComparisonModeSelect({
  disabled = false,
  fullWidth = false,
  layout = 'row',
  compact = false,
}) {
  const {comparisonMode, modeOptions, setComparisonMode, period} = useComparisonMode();
  const isColumn = layout === 'column';
  const selectedLabel = getComparisonModeLabel(period, comparisonMode);

  if (compact) {
    return (
      <Box display="flex" alignItems="center" style={{marginLeft: 4}}>
        <Typography variant="caption" component="span" style={{marginRight: 4, whiteSpace: 'nowrap'}}>
          Compare to
        </Typography>
        <Select
          value={comparisonMode}
          onChange={event => setComparisonMode(event.target.value)}
          disabled={disabled}
          disableUnderline
          displayEmpty
          renderValue={() => (
            <Typography variant="caption" noWrap>
              {selectedLabel.toLowerCase()}
            </Typography>
          )}
          style={{fontSize: '0.75rem', maxWidth: 220}}
          MenuProps={{PaperProps: {style: {maxHeight: '70vh'}}}}
        >
          {modeOptions.map(({value, label, dateContext}) => (
            <MenuItem key={value} value={value} dense style={{paddingTop: 8, paddingBottom: 8}}>
              <div>
                <Typography variant="body2">{label}</Typography>
                {dateContext && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {dateContext}
                  </Typography>
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  return (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth={fullWidth || isColumn}
      disabled={disabled}
      style={isColumn ? {width: '100%'} : {minWidth: 280}}
    >
      <InputLabel id="comparison-mode-label">Comparison basis</InputLabel>
      <Select
        labelId="comparison-mode-label"
        value={comparisonMode}
        label="Comparison basis"
        onChange={event => setComparisonMode(event.target.value)}
        renderValue={() => (
          <Typography variant="body2" noWrap>
            {selectedLabel}
          </Typography>
        )}
      >
        {modeOptions.map(({value, label, dateContext}) => (
          <MenuItem key={value} value={value} style={{paddingTop: 8, paddingBottom: 8}}>
            <div>
              <Typography variant="body2">{label}</Typography>
              {dateContext && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {dateContext}
                </Typography>
              )}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
